import React, { useEffect, useRef } from 'react';
import { pdfjs, usePageContext } from 'react-pdf';

const PDF_TEXT_PAINT_OPERATIONS = new Set([
  pdfjs.OPS.showText,
  pdfjs.OPS.showSpacedText,
  pdfjs.OPS.nextLineShowText,
  pdfjs.OPS.nextLineSetSpacingShowText
]);

const PDF_FILL_PATH_OPERATIONS = new Set([
  pdfjs.OPS.fill,
  pdfjs.OPS.eoFill,
  pdfjs.OPS.fillStroke,
  pdfjs.OPS.eoFillStroke,
  pdfjs.OPS.closeFillStroke,
  pdfjs.OPS.closeEOFillStroke
]);

const isNearWhitePdfColor = (color) => {
  if (typeof color === 'string') {
    const match = color.match(/^#([0-9a-f]{6})$/i);
    if (!match) return false;
    const value = Number.parseInt(match[1], 16);
    return ((value >> 16) & 255) >= 238
      && ((value >> 8) & 255) >= 238
      && (value & 255) >= 238;
  }

  return Array.isArray(color)
    && color.length >= 3
    && color.slice(0, 3).every((channel) => Number(channel) >= 0.93);
};

const getNearWhiteFillOperationIndexes = (operatorList) => {
  const indexes = new Set();
  const fillColorStack = [];
  let fillIsNearWhite = false;

  operatorList.fnArray.forEach((operation, index) => {
    const args = operatorList.argsArray[index] || [];

    if (operation === pdfjs.OPS.save) {
      fillColorStack.push(fillIsNearWhite);
    } else if (operation === pdfjs.OPS.restore) {
      fillIsNearWhite = fillColorStack.pop() ?? false;
    } else if (operation === pdfjs.OPS.setFillRGBColor) {
      fillIsNearWhite = isNearWhitePdfColor(args.length === 1 ? args[0] : args);
    } else if (operation === pdfjs.OPS.setFillGray) {
      fillIsNearWhite = Number(args[0]) >= 0.93;
    } else if (operation === pdfjs.OPS.setFillCMYKColor) {
      fillIsNearWhite = args.slice(0, 4).every((channel) => Number(channel) <= 0.07);
    }

    const constructedPathOperation = operation === pdfjs.OPS.constructPath
      ? (Array.isArray(args[0]) ? args[0][0] : args[0])
      : null;
    const paintsFill = PDF_FILL_PATH_OPERATIONS.has(operation)
      || PDF_FILL_PATH_OPERATIONS.has(constructedPathOperation)
      || operation === pdfjs.OPS.rawFillPath;

    if (fillIsNearWhite && paintsFill) indexes.add(index);
  });

  return indexes;
};

const DarkModePdfCanvas = () => {
  const pageContext = usePageContext();
  const baseCanvasRef = useRef(null);
  const renderSuccessRef = useRef(pageContext?.onRenderSuccess);
  const renderErrorRef = useRef(pageContext?.onRenderError);
  renderSuccessRef.current = pageContext?.onRenderSuccess;
  renderErrorRef.current = pageContext?.onRenderError;

  useEffect(() => {
    const page = pageContext?.page;
    const baseCanvas = baseCanvasRef.current;
    if (!page || !baseCanvas) return undefined;

    const devicePixelRatio = pageContext.devicePixelRatio
      || (typeof window !== 'undefined' ? window.devicePixelRatio : 1)
      || 1;
    const renderViewport = page.getViewport({
      scale: pageContext.scale * devicePixelRatio,
      rotation: pageContext.rotate
    });
    const viewport = page.getViewport({
      scale: pageContext.scale,
      rotation: pageContext.rotate
    });
    let baseRenderTask = null;
    let cancelled = false;

    baseCanvas.width = Math.ceil(renderViewport.width);
    baseCanvas.height = Math.ceil(renderViewport.height);
    baseCanvas.style.width = `${Math.floor(viewport.width)}px`;
    baseCanvas.style.height = `${Math.floor(viewport.height)}px`;
    baseCanvas.style.visibility = 'hidden';

    const renderPage = async () => {
      try {
        const operatorList = await page.getOperatorList();
        if (cancelled) return;
        const nearWhiteFillOperationIndexes = getNearWhiteFillOperationIndexes(operatorList);

        baseRenderTask = page.render({
          canvas: baseCanvas,
          canvasContext: baseCanvas.getContext('2d', { alpha: false }),
          viewport: renderViewport,
          background: '#242424',
          annotationMode: pdfjs.AnnotationMode.ENABLE,
          operationsFilter: (operationIndex) => (
            !PDF_TEXT_PAINT_OPERATIONS.has(operatorList.fnArray[operationIndex])
            && !nearWhiteFillOperationIndexes.has(operationIndex)
          )
        });

        await baseRenderTask.promise;
        if (cancelled) return;
        baseCanvas.style.visibility = '';
        renderSuccessRef.current?.();
      } catch (err) {
        if (cancelled || err?.name === 'RenderingCancelledException' || err?.name === 'AbortException') return;
        renderErrorRef.current?.(err);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      baseRenderTask?.cancel();
      baseCanvas.width = 0;
      baseCanvas.height = 0;
    };
  }, [pageContext?.devicePixelRatio, pageContext?.page, pageContext?.rotate, pageContext?.scale]);

  return (
    <canvas
      ref={baseCanvasRef}
      className="react-pdf__Page__canvas cao-document-viewer__dark-pdf-canvas"
      dir="ltr"
      style={{ display: 'block', userSelect: 'none' }}
    />
  );
};

export default DarkModePdfCanvas;
